import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { VectorMap } from "@react-jvectormap/core";
import worldMill from "./worldMill.json";
import "./styles.scss";

const SEVERITY_LABELS = { 1: "high", 2: "medium", 3: "low" };

const buildCountryData = (networkActivityMap, snortSuricataAlerts) => {
  const country = {};
  const countryDetails = {};

  const ensureCountry = (code, name) => {
    if (!countryDetails[code]) {
      countryDetails[code] = {
        name: name || code,
        ips: [],
        connections: [],
        alertCount: 0,
        maxSeverity: null,
      };
    }
    return countryDetails[code];
  };

  (networkActivityMap || []).forEach((na) => {
    const code = na["country_code"];
    if (!code) return;

    country[code] = (country[code] || 0) + 1;

    const info = ensureCountry(code, na.country_name);
    if (na.ip && !info.ips.includes(na.ip)) info.ips.push(na.ip);

    const connectionKey = `${na.ip}:${na.port}:${na.sni || na.hostname || ""}`;
    if (!info.connections.some((connection) => connection.key === connectionKey)) {
      info.connections.push({
        key: connectionKey,
        ip: na.ip,
        port: na.port,
        protocol: na.protocol,
        sni: na.sni || na.hostname || "",
      });
    }
  });

  // Countries that only appear in network alerts are marked too
  (snortSuricataAlerts || []).forEach((alert) => {
    const code = alert.country_code;
    if (!code) return;

    country[code] = country[code] || 1;

    const info = ensureCountry(code, alert.country_name);
    info.alertCount += 1;
    // Snort severity: lower number is more severe
    if (info.maxSeverity === null || alert.severity < info.maxSeverity) {
      info.maxSeverity = alert.severity;
    }
  });

  return { country, countryDetails };
};

const MARKED_COUNTRY_SCALE = ["#f0707e", "#d21f33"];
const MAP_FOCUS = { scale: 1.5, lng: 7.0926, lat: 31.7917 };

// The label style lives here instead of in styles.scss on purpose: html2canvas
// serializes the svg on its own when it rasterizes the pdf, which drops external
// css but keeps svg attributes. jvectormap applies these keys as attributes.
const REGION_LABEL_STYLE = {
  initial: {
    fill: "#ffffff",
    "font-family": "Nunito, sans-serif",
    "font-size": "10",
    "font-weight": "700",
    stroke: "rgba(0, 0, 0, 0.75)",
    "stroke-width": "2",
    "paint-order": "stroke",
    // Let clicks through to the country underneath
    "pointer-events": "none",
  },
};

// The only intentional differences between the three places the map is used.
// Everything else has to stay shared, otherwise the pdf drifts away from the
// network tab again the next time the map is touched.
// Only the pdf is static: html2canvas rasterizes it, so zoom buttons and the
// country panel would just be dead pixels in the downloaded report.
const MAP_VARIANTS = {
  networkTab: {
    backgroundColor: "var(--matrix-table-color)",
    regionFill: "var(--map-region-color)",
    interactive: true,
  },
  reportPreview: {
    backgroundColor: "var(--matrix-table-color)",
    regionFill: "var(--map-region-color)",
    interactive: true,
  },
  reportPdf: {
    backgroundColor: "none",
    regionFill: "var(--text-color-card)",
    interactive: false,
  },
};

// The react-jvectormap wrapper re-creates the whole map every time it renders,
// which stacks duplicate canvases/tooltips and breaks mouse interactions.
// Keep the canvas in a memoized component with stable props so it renders
// once per data change, and remount it cleanly (via key) when the data changes.
const NetworkMapCanvas = React.memo(function NetworkMapCanvas({
  variant,
  country,
  countryDetails,
  onCountrySelect,
}) {
  const { backgroundColor, regionFill, interactive } = MAP_VARIANTS[variant];

  // Remove the body-level tooltip element jvectormap leaves behind
  useEffect(() => {
    return () => {
      document.querySelectorAll(".jvectormap-tip").forEach((tip) => tip.remove());
    };
  }, []);

  // No mouseover tooltip: country details are shown via click only
  const regionTipHandler = (event) => {
    event.preventDefault();
  };

  const regionLabelRender = (code) => {
    const info = countryDetails[code];
    if (!info || info.ips.length === 0) return "";
    return info.ips.length === 1 ? info.ips[0] : `${info.ips.length} IPs`;
  };

  return (
    <VectorMap
      backgroundColor={backgroundColor}
      map={worldMill}
      zoomMin={1}
      zoomMax={interactive ? 8 : 6}
      zoomStep={interactive ? 1.6 : 0}
      focusOn={MAP_FOCUS}
      zoomOnScroll={interactive}
      zoomButtons={interactive}
      panOnDrag={interactive}
      zoomAnimate={false}
      regionsSelectable={false}
      markersSelectable={false}
      onRegionTipShow={regionTipHandler}
      onRegionClick={interactive ? (event, code) => onCountrySelect(code) : undefined}
      series={{
        regions: [
          {
            values: country,
            scale: MARKED_COUNTRY_SCALE,
            attribute: "fill",
          },
        ],
      }}
      labels={{
        regions: {
          render: regionLabelRender,
        },
      }}
      regionLabelStyle={REGION_LABEL_STYLE}
      containerStyle={{
        width: "100%",
        height: "100%",
      }}
      regionStyle={{
        initial: {
          fill: regionFill,
        },
        hover: {
          fillOpacity: 0.85,
          cursor: "pointer",
        },
        selected: {
          fill: "var(--header-search-text-color)",
        },
      }}
    />
  );
});

const Map = ({ report = false, reportPdf = false }) => {
  const networkActivityMap = useSelector((state) => state.analysisReports.networkActivityMap);
  const snortSuricataAlerts = useSelector((state) => state.analysisReports.snortSuricataAlerts);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const { country, countryDetails } = useMemo(
    () => buildCountryData(networkActivityMap, snortSuricataAlerts),
    [networkActivityMap, snortSuricataAlerts],
  );

  // Force a clean remount of the canvas when the marked countries change
  const mapKey = useMemo(
    () =>
      Object.entries(country)
        .map(([code, value]) => `${code}:${value}`)
        .join("|") || "empty",
    [country],
  );

  const onCountrySelect = useCallback((code) => {
    setSelectedCountry((prevCode) => (prevCode === code ? null : code));
  }, []);

  const selectedInfo = selectedCountry ? countryDetails[selectedCountry] : null;

  const isReportVariant = report || reportPdf;
  const variant = reportPdf ? "reportPdf" : report ? "reportPreview" : "networkTab";
  const isInteractive = MAP_VARIANTS[variant].interactive;

  return (
    <div className="map border">
      <div className={isReportVariant ? "map-report-body" : "map-body"}>
        {Object.keys(country).length === 0 && (
          <p
            className={
              isReportVariant ? "map-report-body__no-region-message" : "map-body__no-region-message"
            }>
            No Region Detected
          </p>
        )}
        <NetworkMapCanvas
          key={mapKey}
          variant={variant}
          country={country}
          countryDetails={countryDetails}
          onCountrySelect={onCountrySelect}
        />
        {isInteractive && selectedInfo ? (
          <div className="map-body__country-panel">
            <div className="map-body__country-panel__title">
              <span>{selectedInfo.name}</span>
              <button
                type="button"
                className="map-body__country-panel__close"
                onClick={() => setSelectedCountry(null)}>
                ✕
              </button>
            </div>
            {selectedInfo.connections.map((connection) => (
              <div key={connection.key} className="map-body__country-panel__row">
                {connection.ip}:{connection.port} · {connection.protocol || "-"}
                {connection.sni ? ` · ${connection.sni}` : ""}
              </div>
            ))}
            {selectedInfo.alertCount ? (
              <div className="map-body__country-panel__alerts">
                Alerts: {selectedInfo.alertCount}
                {selectedInfo.maxSeverity
                  ? ` (max severity: ${
                      SEVERITY_LABELS[selectedInfo.maxSeverity] || selectedInfo.maxSeverity
                    })`
                  : ""}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="map-footer">
        <div className={report || reportPdf ? "map-footer-report__header" : "map-footer__header"}>
          <p
            className={
              report || reportPdf ? "map-footer-report__header__item" : "map-footer__header-item"
            }>
            Destination IP
          </p>
          <p
            className={
              report || reportPdf ? "map-footer-report__header__item" : "map-footer__header-item"
            }>
            Destination Port
          </p>
          <p
            className={
              report || reportPdf ? "map-footer-report__header__item" : "map-footer__header-item"
            }>
            Sni/Hostname
          </p>
          <p
            className={
              report || reportPdf ? "map-footer-report__header__item" : "map-footer__header-item"
            }>
            Uri
          </p>
          <p
            className={
              report || reportPdf ? "map-footer-report__header__item" : "map-footer__header-item"
            }>
            Country Name
          </p>
        </div>
        <div className={report || reportPdf ? "map-report__scrollable" : "map-scrollable"}>
          {networkActivityMap.length > 0 ? (
            networkActivityMap?.map((na, i) => (
              <div
                key={i + 1}
                className={report || reportPdf ? "map-footer-report__body" : "map-footer__body"}>
                <p
                  className={
                    report || reportPdf ? "map-footer-report__body__item" : "map-footer__body-item"
                  }>
                  {na.ip}
                </p>
                <p
                  className={
                    report || reportPdf ? "map-footer-report__body__item" : "map-footer__body-item"
                  }>
                  {na.port}
                </p>
                <p
                  className={
                    report || reportPdf ? "map-footer-report__body__item" : "map-footer__body-item"
                  }>
                  {na?.sni || na?.hostname}
                </p>
                <p
                  className={
                    report || reportPdf ? "map-footer-report__body__item" : "map-footer__body-item"
                  }>
                  {na.uri || "-"}
                </p>
                <p
                  className={
                    report || reportPdf ? "map-footer-report__body__item" : "map-footer__body-item"
                  }>
                  {na.country_name || "-"}
                </p>
              </div>
            ))
          ) : report || reportPdf ? null : (
            <p className="map__no-data">No Url Data</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Map;
